// DoubaoRealtimeClient — 豆包端到端实时语音大模型 3.0 的浏览器二进制协议客户端。
//
// 与火山 ASR(VolcAsrClient)同族的二进制帧，但多了一个 `event` 字段(flag 0x04)+ 会话生命周期
// (StartConnection/StartSession/TaskRequest/SayHello/FinishSession/FinishConnection)。网关对
// 上行帧纯透传，故这里编的帧直达豆包上游；下行帧由这里解析成 { event, payload, audio }。
//
// 帧布局(大端)：
//   header(4B): [0x11, (msgType<<4)|flags, (serialization<<4)|compression, 0x00]
//   [event(4B, int32)]            — flags & 0x04
//   [sessionIdSize(4B)+sessionId] — FULL 帧(msgType 9)/会话级客户端帧带
//   [payloadSize(4B)+payload]
// msgType: 1=FULL_CLIENT_REQUEST, 2=AUDIO_ONLY_CLIENT_REQUEST, 9=FULL_SERVER_RESPONSE,
//          11=AUDIO_ONLY_RESPONSE(TTS), 15=ERROR。
//
// 与后端 apps/ws-gateway/src/adapters/doubao-realtime.adapter.ts 解帧逻辑对称(2026-07-22 实抓验证)。

import { gunzip } from './gzip.js';

// 自带字节助手(不 import ws-adapter-client 的 wsBytes，避免与其 REGISTRY 的循环依赖)。
const concatBytes = (...arrs) => {
  const total = arrs.reduce((n, a) => n + a.byteLength, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrs) { out.set(new Uint8Array(a), off); off += a.byteLength; }
  return out;
};
const toArrayBuffer = (u8) => u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength);
const enc = new TextEncoder();
const dec = new TextDecoder();

// 消息类型(header 高 4 位)
const MSG_FULL_CLIENT_REQUEST = 1;
const MSG_AUDIO_ONLY_REQUEST = 2;

// 客户端事件
export const DoubaoEvent = {
  StartConnection: 1,
  FinishConnection: 2,
  StartSession: 100,
  FinishSession: 102,
  TaskRequest: 200, // 上行音频
  SayHello: 300,
  ChatTTSText: 500,
};
// 服务端事件
export const DoubaoServerEvent = {
  ConnectionStarted: 50,
  ConnectionFailed: 51,
  SessionStarted: 150,
  SessionFinished: 152,
  SessionFailed: 153,
  TTSSentenceStart: 350,
  TTSSentenceEnd: 351,
  TTSResponse: 352, // 下行音频
  TTSEnded: 359,
  ASRResponse: 451, // 用户转写
  ChatResponse: 550, // 模型回复文本
  Usage: 154, // 用量(计费)
};

export class DoubaoRealtimeClient {
  constructor() {
    this.name = 'doubao-realtime';
    this.clientProtocol = 'binary';
  }

  // ── 帧编码 ──────────────────────────────────────────────────────────────
  _frame(msgType, event, sessionId, payloadBytes, serialization, compression) {
    const header = new Uint8Array([
      0x11,
      (msgType << 4) | 0x04, // 恒带 event flag
      (serialization << 4) | compression,
      0x00,
    ]);
    const ev = new Uint8Array(4);
    new DataView(ev.buffer).setInt32(0, event, false);
    const parts = [header, ev];
    if (sessionId != null) {
      const sid = enc.encode(sessionId);
      const ss = new Uint8Array(4);
      new DataView(ss.buffer).setInt32(0, sid.length, false);
      parts.push(ss, sid);
    }
    const ps = new Uint8Array(4);
    new DataView(ps.buffer).setUint32(0, payloadBytes.byteLength, false);
    parts.push(ps, payloadBytes);
    return toArrayBuffer(concatBytes(...parts));
  }

  // 控制帧(JSON, 不压缩): msgType=1(FULL_CLIENT_REQUEST)
  _control(event, sessionId, obj) {
    return this._frame(MSG_FULL_CLIENT_REQUEST, event, sessionId, enc.encode(JSON.stringify(obj || {})), 1, 0);
  }

  startConnection() { return this._control(DoubaoEvent.StartConnection, null, {}); }
  startSession(sessionId, config) { return this._control(DoubaoEvent.StartSession, sessionId, config || {}); }
  sayHello(sessionId, content) { return this._control(DoubaoEvent.SayHello, sessionId, { content }); }
  // 双向流式 TTS：发待合成文本（TaskRequest=200，FULL 请求，text 在 req_params.text）。
  taskRequestText(sessionId, text) { return this._control(DoubaoEvent.TaskRequest, sessionId, { namespace: 'BidirectionalTTS', req_params: { text } }); }
  chatTTSText(sessionId, content) { return this._control(DoubaoEvent.ChatTTSText, sessionId, { content }); }
  finishSession(sessionId) { return this._control(DoubaoEvent.FinishSession, sessionId, {}); }
  finishConnection() { return this._control(DoubaoEvent.FinishConnection, null, {}); }

  // 上行音频帧：msgType=2(AUDIO_ONLY_CLIENT_REQUEST) + event=200(TaskRequest)。
  // raw PCM(不压缩)以保次序、免异步 gzip 乱序。
  taskRequestAudio(sessionId, pcm) {
    const bytes = pcm instanceof Uint8Array ? pcm : new Uint8Array(pcm);
    return this._frame(MSG_AUDIO_ONLY_REQUEST, DoubaoEvent.TaskRequest, sessionId, bytes, 0, 0);
  }

  // ── 帧解码(下行) ────────────────────────────────────────────────────────
  // 返回 { event, msgType, code?, payload?, audio? }；解析失败返回 { raw:null }。
  async parseServerFrame(data) {
    const u8 = data instanceof Uint8Array ? data : new Uint8Array(data);
    if (u8.length < 4) return { raw: null };
    const view = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    const headerSize = (u8[0] & 0x0f) * 4 || 4;
    const msgType = u8[1] >> 4;
    const flags = u8[1] & 0x0f;
    const serialization = u8[2] >> 4;
    const compression = u8[2] & 0x0f;
    let cur = headerSize;
    if (flags & 0x01) cur += 4; // sequence
    let event;
    if (flags & 0x04) { if (cur + 4 > u8.length) return { msgType }; event = view.getInt32(cur, false); cur += 4; }

    try {
      if (msgType === 11) { // AUDIO_ONLY_RESPONSE(TTS): [sidSize][sessionId][size][audio(PCM Float32 24k)]
        const sidSize = view.getInt32(cur, false); cur += 4 + Math.max(0, sidSize); // 跳 session_id
        const size = view.getUint32(cur, false); cur += 4;
        let audio = u8.subarray(cur, cur + size);
        if (compression === 1) { try { audio = await gunzip(audio); } catch { /* leave */ } }
        return { event, msgType, audio };
      }
      if (msgType === 15) { // ERROR: [code(4)][size(4)][payload]
        const code = view.getUint32(cur, false); cur += 4;
        const size = view.getUint32(cur, false); cur += 4;
        let p = u8.subarray(cur, cur + size);
        if (compression === 1) { try { p = await gunzip(p); } catch {} }
        let payload = null; try { payload = JSON.parse(dec.decode(p)); } catch {}
        return { event, msgType, code, payload };
      }
      if (msgType === 9) { // FULL_SERVER_RESPONSE: [sidSize][sid][size][payload]
        const sidSize = view.getInt32(cur, false); cur += 4 + Math.max(0, sidSize);
        const size = view.getUint32(cur, false); cur += 4;
        let p = u8.subarray(cur, cur + size);
        if (compression === 1) { try { p = await gunzip(p); } catch {} }
        let payload = null;
        try { payload = serialization === 1 ? JSON.parse(dec.decode(p)) : dec.decode(p); } catch {}
        return { event, msgType, payload };
      }
    } catch { return { event, msgType, raw: null }; }
    return { event, msgType };
  }

  // 从 event=154 payload 抽取 usage(模态 token)。与后端 extractUsageMeter 对称。
  static extractUsage(payload) {
    const u = (payload && (payload.usage || payload)) || {};
    const num = (v) => (typeof v === 'number' && isFinite(v) && v > 0 ? v : 0);
    return {
      inText: num(u.input_text_tokens),
      inAudio: num(u.input_audio_tokens),
      cached: num(u.cached_text_tokens) + num(u.cached_audio_tokens),
      outText: num(u.output_text_tokens),
      outAudio: num(u.output_audio_tokens),
    };
  }
}
